import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  UserIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  CpuChipIcon,
  PlayIcon,
  StopIcon,
  MagnifyingGlassIcon,
  EllipsisVerticalIcon,
  FunnelIcon,
  XCircleIcon,
  NoSymbolIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { useSocket } from '../contexts/SocketContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import toast from 'react-hot-toast';
import GroupStatusSidebarFilters, { sumLeafStatusCounts } from '../components/GroupStatusSidebarFilters';
import ModelParentSidebar, { groupsHiddenByModelParents } from '../components/ModelParentSidebar';
import { mergeBulkStartProgress } from '../utils/bulkStartProgress';
import ChatbotVersionsTab from './ChatbotVersionsTab';

// ---------------------------------------------------------------------------
// Standalone sub-components (adapted from Accounts.js)
// ---------------------------------------------------------------------------

const UserTypeBadge = ({ userType }) => {
  const map = {
    horny: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
    talker: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
    lonely: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
    buyer: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    unknown: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  };
  const t = userType || 'unknown';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[t] || map.unknown}`}>
      {t}
    </span>
  );
};

const LeadProfilePanel = ({ profile, userType, isDarkMode }) => {
  const p = profile || {};
  const items = [
    { label: 'Location', value: p.location },
    { label: 'Work', value: p.work },
    { label: 'Hobbies', value: p.hobbies?.length ? p.hobbies.join(', ') : null },
    { label: 'Social', value: p.socialLife },
    { label: 'Age', value: p.age },
    { label: 'Status', value: p.relationshipStatus },
  ].filter(x => x.value);
  const hasData = items.length > 0 || p.notes || p.highVisualInterest;

  return (
    <div className={`rounded-lg border p-2.5 mb-3 ${
      isDarkMode ? 'bg-gray-900/50 border-gray-700' : 'bg-white border-gray-200'
    }`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Lead profile
        </p>
        <UserTypeBadge userType={userType} />
      </div>
      {!hasData ? (
        <p className="text-xs text-gray-400 italic">No details yet — builds as he chats</p>
      ) : (
        <dl className="space-y-1 text-xs">
          {items.map(({ label, value }) => (
            <div key={label} className="flex gap-2">
              <dt className={`shrink-0 w-16 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{label}</dt>
              <dd className={`flex-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{value}</dd>
            </div>
          ))}
          {p.highVisualInterest && (
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-yellow-300/90' : 'text-yellow-700'}`}>
              High visual interest (selfie sender)
            </p>
          )}
          {p.notes && (
            <p className={`text-xs mt-1 italic ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{p.notes}</p>
          )}
        </dl>
      )}
    </div>
  );
};

const ConversationProgressLine = ({ conversation }) => {
  const numberOfPhases = conversation.modelId?.conversationPhases?.numberOfPhases || 2;
  const enablePostCTAPhases = conversation.modelId?.ctaPhase?.enablePostCTAPhases || false;
  const postCTAPhases = conversation.modelId?.ctaPhase?.postCTAPhases || [];

  const phases = ['initial'];
  for (let i = 1; i <= numberOfPhases; i++) phases.push(`phase_${i}`);
  phases.push('cta');
  if (enablePostCTAPhases && postCTAPhases.length > 0) {
    [...postCTAPhases]
      .sort((a, b) => a.phaseNumber - b.phaseNumber)
      .forEach(p => phases.push(`phase_${p.phaseNumber}`));
  }

  const isCompleted = conversation.conversationPhase === 'completed';
  const currentPhaseIndex = isCompleted
    ? phases.length - 1
    : phases.indexOf(conversation.conversationPhase);
  const progress = isCompleted
    ? 100
    : currentPhaseIndex >= 0
    ? ((currentPhaseIndex + 1) / phases.length) * 100
    : 0;

  const getGradient = (phase) => {
    const map = {
      initial:  'from-blue-500 to-blue-600',
      phase_1:  'from-blue-500 via-yellow-500 to-yellow-600',
      phase_2:  'from-blue-500 via-yellow-500 via-orange-500 to-orange-600',
      phase_3:  'from-blue-500 via-yellow-500 via-orange-500 via-pink-500 to-pink-600',
      phase_4:  'from-blue-500 via-yellow-500 via-orange-500 via-pink-500 via-purple-500 to-purple-600',
      phase_5:  'from-blue-500 via-yellow-500 via-orange-500 via-pink-500 via-purple-500 via-indigo-500 to-indigo-600',
      cta:      'from-blue-500 via-yellow-500 via-orange-500 via-red-500 to-red-600',
      phase_6:  'from-blue-500 via-yellow-500 via-orange-500 via-red-500 via-rose-500 to-rose-600',
      phase_7:  'from-blue-500 via-yellow-500 via-orange-500 via-red-500 via-rose-500 via-fuchsia-500 to-fuchsia-600',
    };
    return map[phase] || 'from-gray-500 to-gray-600';
  };

  const getLabel = (phase) => {
    if (phase === 'initial') return 'Initial';
    if (phase === 'cta') return 'CTA';
    const m = phase.match(/phase_(\d+)/);
    if (!m) return phase;
    const n = parseInt(m[1]);
    return n <= numberOfPhases ? `P${n}` : `P${numberOfPhases + 1 + (n - 6)}`;
  };

  return (
    <div className="w-full h-6 progress-bg rounded-full overflow-hidden relative">
      <div className="absolute inset-0 flex items-center justify-between px-2">
        {phases.map((phase, i) => (
          <span
            key={phase}
            className={`text-xs font-medium transition-colors duration-300 ${
              i <= currentPhaseIndex ? 'text-white drop-shadow-sm' : 'text-muted'
            }`}
          >
            {getLabel(phase)}
          </span>
        ))}
      </div>
      <div
        className={`h-full bg-gradient-to-r ${
          isCompleted ? getGradient(phases[phases.length - 1]) : getGradient(conversation.conversationPhase)
        } transition-all duration-500 ease-out`}
        style={{ width: `${progress}%` }}
      >
        <div className="absolute inset-0 flex items-center justify-between px-2">
          {phases.map((phase, i) => (
            <span
              key={phase}
              className={`text-xs font-medium transition-colors duration-300 ${
                i <= currentPhaseIndex ? 'text-white drop-shadow-sm' : 'text-transparent'
              }`}
            >
              {getLabel(phase)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getPhaseLabel = (phase) => {
  switch (phase) {
    case 'initial':   return 'Initial';
    case 'phase_1':   return 'Phase 1';
    case 'phase_2':   return 'Phase 2';
    case 'cta':       return 'CTA';
    case 'completed': return 'Completed';
    default:          return phase.charAt(0).toUpperCase() + phase.slice(1).replace('_', ' ');
  }
};

const StatCard = ({ label, value, colorClass, loading, onClick, filterActive }) => (
  <div
    className={`card transition-all ${onClick ? 'cursor-pointer hover:shadow-md hover:border-primary-300 dark:hover:border-primary-600' : ''} ${filterActive ? 'ring-2 ring-primary-500' : ''}`}
    onClick={onClick}
  >
    <div className="card-body text-center py-4">
      <p className={`text-3xl font-bold ${colorClass}`}>
        {loading ? '…' : (value ?? '—')}
      </p>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{label}</p>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const EMPTY_BULK_PROGRESS = { current: 0, total: 0, currentAccount: null, successCount: 0, failCount: 0, status: null, operationId: null };

const CONV_PAGE_SIZE = 50;
const BL_PAGE_SIZE = 50;
// Full phase enum from the Conversation model — the dropdown must offer every
// phase, not just those present on the currently loaded page.
const CONVERSATION_PHASES = ['initial', 'phase_1', 'phase_2', 'phase_3', 'phase_4', 'phase_5', 'phase_6', 'phase_7', 'cta', 'followup', 'completed'];

const ERROR_TYPE_LABELS = {
  xchat_key_error:   'XChat Key Error',
  bulk_start_failed: 'Bulk Start Failed',
  reply_failed:      'Reply Failed',
  process_dm_error:  'DM Processing Error',
  chatbot_error:     'Chatbot Error',
};

const ERROR_TYPE_META = {
  xchat_key_error:   { label: 'XChat Key Error',    badge: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300', border: 'border-orange-400', text: 'text-orange-700 dark:text-orange-300' },
  bulk_start_failed: { label: 'Bulk Start Failed',   badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',           border: 'border-red-500',    text: 'text-red-700 dark:text-red-300' },
  reply_failed:      { label: 'Reply Failed',         badge: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300', border: 'border-yellow-400', text: 'text-yellow-700 dark:text-yellow-300' },
  process_dm_error:  { label: 'DM Processing Error', badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',           border: 'border-red-400',    text: 'text-red-700 dark:text-red-300' },
  chatbot_error:     { label: 'Chatbot Error',        badge: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',          border: 'border-gray-400',   text: 'text-gray-700 dark:text-gray-300' },
};

const Chatbot = () => {
  // eslint-disable-next-line no-unused-vars
  const { isDarkMode } = useTheme();
  const { socket } = useSocket();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // -- Stats --
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // -- Tabs --
  const [activeTab, setActiveTab] = useState('control');

  // -- Accounts (for filter dropdown) --
  const [accounts, setAccounts] = useState([]);

  // -- Models (for model filter) --
  const [models, setModels] = useState([]);

  // -- Control Panel --
  const [cpAccounts, setCpAccounts] = useState([]);
  const [cpLoading, setCpLoading] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState(new Set());
  const [cpOperationLoading, setCpOperationLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modelFilter, setModelFilter] = useState('all');
  const [vpsFilter, setVpsFilter] = useState('all');
  const [automationFilter, setAutomationFilter] = useState('all');
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [modelGroups, setModelGroups] = useState([]); // [{modelId, modelName, total, groups:[{name, total, ...}]}]
  const [collapsedModels, setCollapsedModels] = useState({});
  const [cpPage, setCpPage] = useState(1);
  const [cpTotalCount, setCpTotalCount] = useState(0);
  const [groupDetailMap, setGroupDetailMap] = useState({});
  const [leafGroupCounts, setLeafGroupCounts] = useState({});
  const [leafGroupStatusCounts, setLeafGroupStatusCounts] = useState({});
  const [totalAccountsCount, setTotalAccountsCount] = useState(0);
  const [allAccountsSelected, setAllAccountsSelected] = useState(false);
  const [addingGroup, setAddingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [openGroupMenu, setOpenGroupMenu] = useState(null);
  const [bulkStartProgress, setBulkStartProgress] = useState(EMPTY_BULK_PROGRESS);
  const bulkStartInProgressRef = useRef(false);

  // -- Conversations --
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [convDetailLoadingId, setConvDetailLoadingId] = useState(null);
  const [conversationFilter, setConversationFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [phaseFilter, setPhaseFilter] = useState('all');
  const [convAccountStatusFilter, setConvAccountStatusFilter] = useState('all');
  const [expandedConvId, setExpandedConvId] = useState(null);
  const [convPage, setConvPage] = useState(1);
  const [convTotal, setConvTotal] = useState(0);
  const conversationsListRef = useRef(null);
  const conversationsScrollPositionRef = useRef(0);

  // -- Error log --
  const [errorLog, setErrorLog] = useState([]);
  const [expandedErrors, setExpandedErrors] = useState(new Set());
  const [errorTypeFilter, setErrorTypeFilter] = useState('all');

  // -- Blacklist (admin) --
  const [blacklistUsers, setBlacklistUsers] = useState([]);
  const [blacklistLoading, setBlacklistLoading] = useState(false);
  const [blacklistPage, setBlacklistPage] = useState(1);
  const [blacklistTotal, setBlacklistTotal] = useState(0);
  const [blacklistSearch, setBlacklistSearch] = useState('');
  const [blacklistSearchInput, setBlacklistSearchInput] = useState('');
  const [selectedBlacklist, setSelectedBlacklist] = useState(new Set());
  const [expandedBlacklistId, setExpandedBlacklistId] = useState(null);
  const [blacklistUnblockLoading, setBlacklistUnblockLoading] = useState(false);
  const [spamBlacklistEnabled, setSpamBlacklistEnabled] = useState(false);
  const [convIdSwitchFilterEnabled, setConvIdSwitchFilterEnabled] = useState(false);
  const [spamBlacklistLoading, setSpamBlacklistLoading] = useState(false);
  const [spamBlacklistSaving, setSpamBlacklistSaving] = useState(false);
  const [convIdSwitchFilterSaving, setConvIdSwitchFilterSaving] = useState(false);

  // ---- Stats ----------------------------------------------------------------

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await api.get('/accounts/chatbot-stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch chatbot stats', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  // ---- Accounts list (conversations filter only — id + name) ---------------

  useEffect(() => {
    if (activeTab !== 'conversations') return undefined;
    let cancelled = false;
    api.get('/accounts?picker=1')
      .then(res => {
        if (!cancelled) setAccounts(res.data.accounts || res.data || []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [activeTab]);

  // ---- Control Panel accounts -----------------------------------------------

  const fetchGroupDetail = useCallback(() => {
    api.get('/accounts/groups').then(res => {
      const data = res.data || {};
      const allGroups = data.groups || [];
      const leafGroupsDetailed = data.leafGroupsDetailed || [];
      const mainGroups = data.mainGroups || [];
      const map = {};
      leafGroupsDetailed.forEach(({ name, ...detail }) => {
        map[name] = detail;
      });
      mainGroups.forEach(mg => {
        if (!map[mg.name]) map[mg.name] = {};
        if (Array.isArray(mg.subGroups)) map[mg.name].subGroups = mg.subGroups;
        if (mg.automation) map[mg.name].automation = mg.automation;
        if (mg.attachedAgent) map[mg.name].attachedAgent = mg.attachedAgent;
      });
      setGroupDetailMap(map);
      setGroups(allGroups);
      setModelGroups(Array.isArray(data.models) ? data.models : []);
    }).catch(() => {});
  }, []);

  const fetchCpAccounts = useCallback(async () => {
    setCpLoading(true);
    try {
      const params = new URLSearchParams({ lightweight: 'true', paginate: 'true', page: String(cpPage), limit: '50' });
      if (selectedGroup && selectedGroup !== 'all') params.set('filterGroup', selectedGroup);
      if (statusFilter && statusFilter !== 'all') params.set('filterStatus', statusFilter);
      if (modelFilter && modelFilter !== 'all') params.set('filterModelId', modelFilter);
      if (vpsFilter && vpsFilter !== 'all') params.set('filterVpsAgentId', vpsFilter);
      if (automationFilter && automationFilter !== 'all') params.set('filterAutomation', automationFilter);
      if (searchQuery.trim()) params.set('search', searchQuery.trim());
      const res = await api.get(`/accounts?${params}`);
      const data = res.data;
      const list = data.accounts || [];
      setCpAccounts(list);
      setCpTotalCount(data.pagination?.total || 0);
    } catch (err) {
      console.error('Failed to fetch CP accounts', err);
    } finally {
      setCpLoading(false);
    }
  }, [cpPage, selectedGroup, statusFilter, modelFilter, vpsFilter, automationFilter, searchQuery]);

  useEffect(() => {
    fetchCpAccounts();
  }, [fetchCpAccounts]);

  // Reset to page 1 whenever any filter changes
  useEffect(() => { setCpPage(1); }, [selectedGroup, statusFilter, modelFilter, vpsFilter, automationFilter, searchQuery]);

  useEffect(() => {
    fetchGroupDetail();
    api.get('/accounts/summary').then(res => {
      setLeafGroupCounts(res.data.leafGroupCounts || {});
      setLeafGroupStatusCounts(res.data.leafGroupStatusCounts || {});
      setTotalAccountsCount(res.data.totalAccounts || 0);
    }).catch(() => {});
  }, [fetchGroupDetail]);

  // Fetch models for the filter dropdown
  useEffect(() => {
    api.get('/models?minimal=1').then(res => {
      setModels(res.data || []);
    }).catch(() => {});
  }, []);

  // ---- Bulk start / stop ----------------------------------------------------

  const handleBulkStart = async (ids) => {
    if (!ids || ids.length === 0) { toast.error('Select accounts first'); return; }
    // No client-side model grouping: ids may span pages the browser never
    // loaded (e.g. "Select all 1513 accounts"), so we can't resolve models
    // here. The backend starts each account with its own assigned model and
    // reports missing-model accounts as failures in the progress events.
    setCpOperationLoading(true);
    bulkStartInProgressRef.current = true;
    const alreadyRunning = bulkStartProgress.status === 'queued' || bulkStartProgress.status === 'processing';
    if (!alreadyRunning) {
      setBulkStartProgress({ ...EMPTY_BULK_PROGRESS, total: ids.length, status: 'queued' });
    } else {
      setBulkStartProgress((prev) => ({
        ...prev,
        currentAccount: `Adding ${ids.length} account(s) to the running start…`,
      }));
    }
    try {
      const res = await api.post('/accounts/bulk-start', { accountIds: ids });
      if (res.data?.operationId) {
        const opId = res.data.operationId;
        setBulkStartProgress(prev => ({ ...prev, operationId: opId }));
      }
      const added = res.data?.added != null ? res.data.added : ids.length;
      toast.success(res.data?.appended
        ? (added === 0
          ? 'Those accounts are already in the running start'
          : `Added ${added} account${added !== 1 ? 's' : ''} to the running start`)
        : `Queued ${ids.length} account${ids.length !== 1 ? 's' : ''} for startup`);
      setSelectedAccounts(new Set());
      setCpAccounts(prev => prev.map(a =>
        ids.includes(String(a._id)) ? { ...a, automation: { ...a.automation, enabled: true, starting: true } } : a
      ));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start');
      bulkStartInProgressRef.current = false;
      setBulkStartProgress(EMPTY_BULK_PROGRESS);
    } finally {
      setCpOperationLoading(false);
    }
  };

  const handleBulkStartCancel = async () => {
    try {
      const res = await api.post('/accounts/bulk-start/cancel');
      toast.success(res.data?.message || 'Bulk start cancelled');
      bulkStartInProgressRef.current = false;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel');
    }
  };

  const handleBulkStop = async (ids) => {
    if (!ids || ids.length === 0) { toast.error('Select accounts first'); return; }
    setCpOperationLoading(true);
    try {
      await api.post('/accounts/bulk-stop', { accountIds: ids });
      toast.success(`Stopping ${ids.length} account${ids.length !== 1 ? 's' : ''}`);
      setSelectedAccounts(new Set());
      setCpAccounts(prev => prev.map(a =>
        ids.includes(String(a._id)) ? { ...a, automation: { ...a.automation, enabled: false, starting: false } } : a
      ));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to stop');
    } finally {
      setCpOperationLoading(false);
    }
  };

  // ---- Filtered CP accounts -------------------------------------------------
  // All filtering is now server-side; this is a passthrough kept for call-site compatibility.
  const getFilteredCpAccounts = () => cpAccounts;

  const hasActiveFilters = statusFilter !== 'all' || modelFilter !== 'all' || vpsFilter !== 'all' || automationFilter !== 'all' || searchQuery.trim();

  const clearFilters = () => {
    setStatusFilter('all');
    setModelFilter('all');
    setVpsFilter('all');
    setAutomationFilter('all');
    setSearchQuery('');
  };

  // Fetch all IDs matching current filters (across all pages) for Start All / Stop All
  const fetchAllFilteredIds = async () => {
    const params = new URLSearchParams({ lightweight: 'true' });
    if (selectedGroup !== 'all') params.set('filterGroup', selectedGroup);
    if (statusFilter !== 'all') params.set('filterStatus', statusFilter);
    if (modelFilter !== 'all') params.set('filterModelId', modelFilter);
    if (vpsFilter !== 'all') params.set('filterVpsAgentId', vpsFilter);
    if (automationFilter !== 'all') params.set('filterAutomation', automationFilter);
    if (searchQuery.trim()) params.set('search', searchQuery.trim());
    const res = await api.get(`/accounts/ids?${params}`);
    return (res.data.ids || res.data || []).map(String);
  };

  // ---- Status badge helpers -------------------------------------------------

  const getStatusBadge = (account) => {
    const map = {
      active: 'badge-green',
      suspended: 'badge-danger',
      captcha: 'badge-warning',
      xchat_key_error: 'badge-warning',
      auth_expired: 'badge-danger',
      session_extraction_failed: 'badge-danger',
      login_failed: 'badge-danger',
      inactive: 'badge-gray',
    };
    return map[account.status] || 'badge-gray';
  };

  const getStatusLabel = (s) => {
    const labels = {
      active: 'Active', suspended: 'Suspended', captcha: 'Captcha',
      xchat_key_error: 'Key Error', auth_expired: 'Auth Expired',
      session_extraction_failed: 'Session Failed', login_failed: 'Login Failed',
      inactive: 'Inactive',
    };
    return labels[s] || s;
  };

  // ---- Conversations --------------------------------------------------------
  // Server-side pagination + filtering: filters apply to ALL conversations in
  // the DB (not just the rows in memory), sorted newest-first by the backend.

  const fetchConversations = useCallback(async () => {
    conversationsScrollPositionRef.current = conversationsListRef.current?.scrollTop ?? 0;
    setConversationsLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(CONV_PAGE_SIZE),
        offset: String((convPage - 1) * CONV_PAGE_SIZE),
        omitMessages: '1'
      });
      if (conversationFilter !== 'all') params.set('accountId', conversationFilter);
      if (sourceFilter !== 'all') params.set('source', sourceFilter);
      if (phaseFilter !== 'all') params.set('phase', phaseFilter);
      if (convAccountStatusFilter !== 'all') params.set('accountStatus', convAccountStatusFilter);
      const res = await api.get(`/conversations?${params}`);
      setConversations(res.data.conversations || []);
      setConvTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to fetch conversations');
    } finally {
      setConversationsLoading(false);
    }
  }, [convPage, conversationFilter, sourceFilter, phaseFilter, convAccountStatusFilter]);

  const toggleConversationExpand = async (conversationId) => {
    if (expandedConvId === conversationId) {
      setExpandedConvId(null);
      return;
    }
    setExpandedConvId(conversationId);
    const existing = conversations.find((c) => String(c._id) === String(conversationId));
    if (existing?.messages) return;
    setConvDetailLoadingId(conversationId);
    try {
      const res = await api.get(`/conversations/${conversationId}`);
      const full = res.data || {};
      setConversations((prev) => prev.map((c) => (
        String(c._id) === String(conversationId)
          ? {
              ...c,
              messages: full.messages || [],
              userProfile: full.userProfile ?? c.userProfile,
              userType: full.userType ?? c.userType,
              currentDay: full.currentDay ?? c.currentDay
            }
          : c
      )));
    } catch {
      toast.error('Failed to load messages');
    } finally {
      setConvDetailLoadingId((id) => (id === conversationId ? null : id));
    }
  };

  // ---- Blacklist (admin) ----------------------------------------------------

  const fetchBlacklistedUsers = useCallback(async () => {
    if (!isAdmin) return;
    setBlacklistLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(blacklistPage),
        limit: String(BL_PAGE_SIZE),
        messageLimit: '10',
      });
      if (blacklistSearch.trim()) params.set('search', blacklistSearch.trim());
      const res = await api.get(`/admin/chatbot/blacklisted-users?${params}`);
      const users = (res.data.users || []).map((u) => ({
        ...u,
        // Keep X user IDs as strings — snowflake IDs lose precision as JS numbers
        xUserId: u.xUserId != null ? String(u.xUserId) : '',
      }));
      setBlacklistUsers(users);
      setBlacklistTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to fetch blacklist');
    } finally {
      setBlacklistLoading(false);
    }
  }, [isAdmin, blacklistPage, blacklistSearch]);

  useEffect(() => {
    if (isAdmin) {
      api.get('/admin/chatbot/blacklisted-users?limit=1')
        .then(res => setBlacklistTotal(res.data.total || 0))
        .catch(() => {});
    }
  }, [isAdmin]);

  const fetchBlacklistSettings = useCallback(async () => {
    if (!isAdmin) return;
    setSpamBlacklistLoading(true);
    try {
      const res = await api.get('/admin/chatbot/blacklist-settings');
      setSpamBlacklistEnabled(!!res.data.spamBlacklistEnabled);
      setConvIdSwitchFilterEnabled(!!res.data.convIdSwitchFilterEnabled);
    } catch {
      toast.error('Failed to load blacklist settings');
    } finally {
      setSpamBlacklistLoading(false);
    }
  }, [isAdmin]);

  const handleSpamBlacklistToggle = async (enabled) => {
    setSpamBlacklistSaving(true);
    try {
      const res = await api.patch('/admin/chatbot/blacklist-settings', { spamBlacklistEnabled: enabled });
      setSpamBlacklistEnabled(!!res.data.spamBlacklistEnabled);
      toast.success(res.data.message || (enabled ? 'Automatic blacklist enabled' : 'Automatic blacklist disabled'));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update blacklist setting');
    } finally {
      setSpamBlacklistSaving(false);
    }
  };

  const handleConvIdSwitchFilterToggle = async (enabled) => {
    setConvIdSwitchFilterSaving(true);
    try {
      const res = await api.patch('/admin/chatbot/blacklist-settings', { convIdSwitchFilterEnabled: enabled });
      setConvIdSwitchFilterEnabled(!!res.data.convIdSwitchFilterEnabled);
      toast.success(res.data.message || (enabled ? 'Bot filter (conv ID switch) enabled' : 'Bot filter (conv ID switch) disabled'));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update bot filter');
    } finally {
      setConvIdSwitchFilterSaving(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'blacklist' && isAdmin) {
      fetchBlacklistSettings();
      fetchBlacklistedUsers();
    }
  }, [activeTab, isAdmin, fetchBlacklistedUsers, fetchBlacklistSettings]);

  useEffect(() => {
    setBlacklistPage(1);
  }, [blacklistSearch]);

  const toggleBlacklistSelection = (xUserId) => {
    const id = String(xUserId || '').trim();
    if (!id) return;
    setSelectedBlacklist(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllBlacklistOnPage = () => {
    if (blacklistUsers.length === 0) return;
    const allSelected = blacklistUsers.every(u => selectedBlacklist.has(u.xUserId));
    if (allSelected) {
      setSelectedBlacklist(prev => {
        const next = new Set(prev);
        blacklistUsers.forEach(u => next.delete(u.xUserId));
        return next;
      });
    } else {
      setSelectedBlacklist(prev => {
        const next = new Set(prev);
        blacklistUsers.forEach(u => next.add(u.xUserId));
        return next;
      });
    }
  };

  const handleUnblockBlacklist = async (xUserIds) => {
    const ids = [...new Set((xUserIds || []).map((id) => String(id || '').trim()).filter(Boolean))];
    if (ids.length === 0) return;
    const label = ids.length === 1
      ? (blacklistUsers.find(u => u.xUserId === ids[0])?.xUsername || ids[0])
      : `${ids.length} users`;
    if (!window.confirm(`Unblock ${label}? Automation will reset claims and queue inbox scans to reply to the latest message.`)) return;

    setBlacklistUnblockLoading(true);
    try {
      const res = await api.post('/admin/chatbot/blacklisted-users/unblock', { xUserIds: ids });
      const results = res.data.results || [];
      const ok = results.filter(r => r.success).length;
      if (ok === 0) {
        const firstErr = results[0]?.error || res.data.message || 'Unknown error';
        toast.error(`Unblock failed (${firstErr}). Try refreshing the list.`);
      } else if (ok < ids.length) {
        toast.error(`Unblocked ${ok} of ${ids.length} — some entries were not found`);
      } else {
        toast.success(`Unblocked ${ok} user(s) — inbox scans queued`);
      }
      setSelectedBlacklist(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
      fetchBlacklistedUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to unblock');
    } finally {
      setBlacklistUnblockLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'conversations') return;
    fetchConversations();
  }, [activeTab, fetchConversations]);

  // Restore scroll position after conversations update
  useEffect(() => {
    if (!conversationsLoading && conversationsListRef.current) {
      const saved = conversationsScrollPositionRef.current;
      requestAnimationFrame(() => {
        if (conversationsListRef.current) conversationsListRef.current.scrollTop = saved;
      });
    }
  }, [conversations, conversationsLoading]);

  const handleDeleteConversation = async (conversationId) => {
    if (!window.confirm('Are you sure you want to clean this conversation? This will permanently delete all messages and data from the database.')) return;
    try {
      await api.delete(`/conversations/${conversationId}`);
      toast.success('Conversation cleaned successfully');
      fetchConversations();
    } catch {
      toast.error('Failed to clean conversation');
    }
  };

  // ---- Error log socket listener --------------------------------------------

  const appendError = useCallback((entry) => {
    setErrorLog(prev => [entry, ...prev].slice(0, 300));
    // Also switch to the error tab if we're not already there (only once per session)
    // Don't auto-switch — just increment the badge counter silently
  }, []);

  useEffect(() => {
    if (!socket) return;

    // chatbot_error is now emitted by the backend for: bulk start failures,
    // xchat key errors, DM send failures, and unexpected processIncomingDM errors
    const onChatbotError = (data) => {
      appendError({
        id: Date.now() + Math.random(),
        timestamp: data.timestamp || new Date().toISOString(),
        username: data.username || data.accountUsername || 'unknown',
        accountId: data.accountId || null,
        message: data.error || data.message || 'Unknown error',
        type: data.type || 'chatbot_error',
        detail: data.detail || null,
      });
    };

    // automation-started with status:'failed' surfaces bulk start / automation start failures
    const onStartedFailed = (data) => {
      if (data.status !== 'failed') return;
      appendError({
        id: Date.now() + Math.random(),
        timestamp: data.timestamp || new Date().toISOString(),
        username: data.username || 'unknown',
        accountId: data.accountId || null,
        message: data.error || 'Automation start failed',
        type: data.classification || 'bulk_start_failed',
        detail: { classification: data.classification },
      });
    };

    // automation-update with reason:'xchat_key_error' = runtime key failure
    const onAutomationUpdateError = (data) => {
      if (data.reason !== 'xchat_key_error') return;
      appendError({
        id: Date.now() + Math.random(),
        timestamp: new Date().toISOString(),
        username: 'unknown',
        accountId: data.accountId || null,
        message: data.message || 'XChat key error — automation stopped',
        type: 'xchat_key_error',
        detail: null,
      });
    };

    socket.on('chatbot_error', onChatbotError);
    socket.on('xchat_key_error', onChatbotError);
    socket.on('automation-started', onStartedFailed);
    socket.on('automation-update', onAutomationUpdateError);
    return () => {
      socket.off('chatbot_error', onChatbotError);
      socket.off('xchat_key_error', onChatbotError);
      socket.off('automation-started', onStartedFailed);
      socket.off('automation-update', onAutomationUpdateError);
    };
  }, [socket, appendError]);

  // ---- Automation state socket listeners ------------------------------------

  useEffect(() => {
    if (!socket) return;
    if (user) socket.emit('join-user-room', user._id || user.id);

    const onStarted = (data) => {
      setCpAccounts(prev => prev.map(a =>
        String(a._id) === String(data.accountId)
          ? { ...a, automation: { ...a.automation, enabled: true, starting: false }, status: data.status || a.status }
          : a
      ));
    };
    const onStopped = (data) => {
      setCpAccounts(prev => prev.map(a =>
        String(a._id) === String(data.accountId)
          ? { ...a, automation: { ...a.automation, enabled: false, starting: false } }
          : a
      ));
    };
    const onStatusUpdated = (data) => {
      if (!data?.accountId) return;
      setCpAccounts(prev => prev.map(a =>
        String(a._id) === String(data.accountId)
          ? { ...a, status: data.status || a.status, automation: { ...a.automation, ...data.automation } }
          : a
      ));
    };
    const onStatusUpdatedBatch = (updates) => {
      if (!Array.isArray(updates)) return;
      const map = {};
      updates.forEach(d => { if (d.accountId) map[String(d.accountId)] = d; });
      setCpAccounts(prev => prev.map(a => {
        const upd = map[String(a._id)];
        return upd ? { ...a, status: upd.status || a.status, automation: { ...a.automation, ...upd.automation } } : a;
      }));
    };
    const onAutomationUpdate = () => { fetchStats(); };

    const onBulkStartProgress = (p) => {
      setBulkStartProgress((prev) => mergeBulkStartProgress(prev, p));
      if (p.status === 'completed' || p.status === 'cancelled') {
        bulkStartInProgressRef.current = false;
        const msg = p.status === 'completed'
          ? `Bulk start complete — ${p.successCount || 0} started, ${p.failCount || 0} failed`
          : `Bulk start cancelled — ${p.successCount || 0} completed`;
        p.failCount > 0 ? toast.error(msg) : toast.success(msg);
        setTimeout(() => setBulkStartProgress(EMPTY_BULK_PROGRESS), 6000);
        fetchCpAccounts();
      }
    };

    socket.on('automation-started', onStarted);
    socket.on('automation-stopped', onStopped);
    socket.on('account-status-updated', onStatusUpdated);
    socket.on('account-status-updated-batch', onStatusUpdatedBatch);
    socket.on('automation-update', onAutomationUpdate);
    socket.on('bulk-start-progress', onBulkStartProgress);
    return () => {
      socket.off('automation-started', onStarted);
      socket.off('automation-stopped', onStopped);
      socket.off('account-status-updated', onStatusUpdated);
      socket.off('account-status-updated-batch', onStatusUpdatedBatch);
      socket.off('automation-update', onAutomationUpdate);
      socket.off('bulk-start-progress', onBulkStartProgress);
    };
  }, [socket, user, fetchStats, fetchCpAccounts]);

  // Reset allAccountsSelected when selection or filters change
  useEffect(() => {
    setAllAccountsSelected(false);
  }, [selectedAccounts, selectedGroup, statusFilter, modelFilter, vpsFilter, automationFilter, searchQuery]);


  const getGroupCount = (groupName) => {
    const meta = groupDetailMap[groupName];
    if (meta?.subGroups?.length) {
      return meta.subGroups.reduce((sum, sg) => sum + (leafGroupCounts[sg] || 0), 0);
    }
    return leafGroupCounts[groupName] || 0;
  };

  const getGroupStatusCounts = (groupName) => {
    const meta = groupDetailMap[groupName];
    const leaves = meta?.subGroups?.length ? meta.subGroups : [groupName];
    return sumLeafStatusCounts(leaves, leafGroupStatusCounts);
  };

  const toggleErrorExpand = (id) => {
    setExpandedErrors(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Close group kebab menu on outside click
  useEffect(() => {
    const handler = () => setOpenGroupMenu(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const handleRenameGroup = async (groupName) => {
    const newName = window.prompt(`Rename group "${groupName}" to:`, groupName);
    if (!newName || newName.trim() === groupName) return;
    try {
      await api.post('/accounts/rename-group', { oldName: groupName, newName: newName.trim() });
      toast.success(`Group renamed to "${newName.trim()}"`);
      if (selectedGroup === groupName) setSelectedGroup(newName.trim());
      fetchGroupDetail();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to rename group');
    }
  };

  const handleDeleteGroup = async (groupName) => {
    if (!window.confirm(`Delete group "${groupName}"? Accounts will become ungrouped.`)) return;
    try {
      await api.post('/accounts/delete-group', { groupName });
      toast.success(`Group "${groupName}" deleted`);
      if (selectedGroup === groupName) setSelectedGroup('all');
      fetchGroupDetail();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete group');
    }
  };

  // ---- Render ---------------------------------------------------------------

  const convTotalPages = Math.max(1, Math.ceil(convTotal / CONV_PAGE_SIZE));
  const blacklistTotalPages = Math.max(1, Math.ceil(blacklistTotal / BL_PAGE_SIZE));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <CpuChipIcon className="h-7 w-7 text-primary-600 dark:text-primary-400" />
            Chatbot Automation
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Central control panel — start, stop, monitor and filter automations across all accounts
          </p>
        </div>
        <button onClick={() => { fetchStats(); fetchCpAccounts(); }} disabled={cpLoading || statsLoading} className="btn btn-secondary btn-sm">
          <ArrowPathIcon className={`h-4 w-4 mr-1 ${(cpLoading || statsLoading) ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats row — clicking a card quick-filters the table */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard label="Running"        value={stats?.activeAccounts}        colorClass="text-green-600 dark:text-green-400"  loading={statsLoading}
          onClick={() => { setActiveTab('control'); setAutomationFilter(automationFilter === 'running_stable' ? 'all' : 'running_stable'); }}
          filterActive={automationFilter === 'running_stable'} />
        <StatCard label="Total Enabled"  value={stats?.totalAutomationEnabled} colorClass="text-blue-600 dark:text-blue-400"   loading={statsLoading}
          onClick={() => { setActiveTab('control'); setAutomationFilter(automationFilter === 'active' ? 'all' : 'active'); }}
          filterActive={automationFilter === 'active'} />
        <StatCard label="Captcha"        value={stats?.captcha}               colorClass="text-yellow-600 dark:text-yellow-400" loading={statsLoading}
          onClick={() => { setActiveTab('control'); setStatusFilter(statusFilter === 'captcha' ? 'all' : 'captcha'); }}
          filterActive={statusFilter === 'captcha'} />
        <StatCard label="Key Error"      value={stats?.xchatKeyError}         colorClass="text-orange-600 dark:text-orange-400" loading={statsLoading}
          onClick={() => { setActiveTab('control'); setStatusFilter(statusFilter === 'xchat_key_error' ? 'all' : 'xchat_key_error'); }}
          filterActive={statusFilter === 'xchat_key_error'} />
        <StatCard label="Auth Expired"   value={stats?.authExpired}           colorClass="text-red-700 dark:text-red-300"      loading={statsLoading}
          onClick={() => { setActiveTab('control'); setStatusFilter(statusFilter === 'auth_expired' ? 'all' : 'auth_expired'); }}
          filterActive={statusFilter === 'auth_expired'} />
        <StatCard label="Suspended"      value={stats?.suspended}             colorClass="text-red-600 dark:text-red-400"      loading={statsLoading}
          onClick={() => { setActiveTab('control'); setStatusFilter(statusFilter === 'suspended' ? 'all' : 'suspended'); }}
          filterActive={statusFilter === 'suspended'} />
      </div>

      {/* Tab nav */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('control')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'control'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <CpuChipIcon className="h-5 w-5 inline mr-2" />
            Control Panel
          </button>
          <button
            onClick={() => setActiveTab('conversations')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'conversations'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <ChatBubbleLeftRightIcon className="h-5 w-5 inline mr-2" />
            Conversations
          </button>
          <button
            onClick={() => setActiveTab('versions')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'versions'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <ChartBarIcon className="h-5 w-5 inline mr-2" />
            Versions
          </button>
          <button
            onClick={() => setActiveTab('errors')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'errors'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <ExclamationTriangleIcon className="h-5 w-5 inline mr-2" />
            Error Log
            {errorLog.length > 0 && (
              <span className="ml-2 badge badge-danger">{errorLog.length}</span>
            )}
          </button>
          {isAdmin && (
            <button
              onClick={() => setActiveTab('blacklist')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'blacklist'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <NoSymbolIcon className="h-5 w-5 inline mr-2" />
              Blacklist
              {blacklistTotal > 0 && activeTab !== 'blacklist' && (
                <span className="ml-2 badge badge-gray">{blacklistTotal.toLocaleString()}</span>
              )}
            </button>
          )}
        </nav>
      </div>

      {/* ---- Control Panel tab ---- */}
      {activeTab === 'control' && (
        <div className="flex gap-6">
          {/* Groups sidebar */}
          <div className="w-52 flex-shrink-0">
            <div className="card" style={{ overflow: 'visible' }}>
              <div className="card-body p-2">
                <div className="space-y-1">
                  {/* All Groups button */}
                  <button
                    onClick={() => {
                      setSelectedGroup('all');
                      setStatusFilter('all');
                      setModelFilter('all');
                      setVpsFilter('all');
                    }}
                    className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-between ${
                      selectedGroup === 'all' && (!modelFilter || modelFilter === 'all')
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span>All Groups</span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">{totalAccountsCount || cpTotalCount || cpAccounts.length}</span>
                  </button>

                  <ModelParentSidebar
                    models={modelGroups}
                    collapsed={collapsedModels}
                    onToggle={(modelId) => setCollapsedModels((prev) => ({ ...prev, [modelId]: !prev[modelId] }))}
                    selectedModelId={modelFilter}
                    selectedGroup={selectedGroup}
                    filterStatus={statusFilter}
                    filterVpsAgentId={vpsFilter}
                    onSelectModel={(model) => {
                      setModelFilter(model.modelId);
                      setVpsFilter('all');
                      setSelectedGroup('all');
                      setStatusFilter('all');
                    }}
                    onSelectModelStatus={(model, status) => {
                      setModelFilter(model.modelId);
                      setVpsFilter('all');
                      setSelectedGroup('all');
                      setStatusFilter(status);
                    }}
                    onSelectGroup={(model, name) => {
                      setModelFilter(model.modelId);
                      setVpsFilter('all');
                      setSelectedGroup(name);
                      setStatusFilter('all');
                    }}
                    onSelectGroupStatus={(model, name, status) => {
                      setModelFilter(model.modelId);
                      setVpsFilter('all');
                      setSelectedGroup(name);
                      setStatusFilter(status);
                    }}
                    onSelectGroupVps={(model, name, status, vpsId) => {
                      setModelFilter(model.modelId);
                      setVpsFilter(vpsId);
                      setSelectedGroup(name);
                      setStatusFilter(status);
                    }}
                  />

                  {groups.filter((g) => !groupsHiddenByModelParents(modelGroups).has(g)).map(g => {
                    const meta = groupDetailMap[g];
                    const hasPreset = !!meta?.automation;
                    const hasAgent = !!meta?.attachedAgent;
                    return (
                      <div key={g}>
                      <div className="relative group/grp">
                        <button
                          onClick={() => {
                            setSelectedGroup(g);
                            setStatusFilter('all');
                            setModelFilter('all');
                            setVpsFilter('all');
                          }}
                          className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-between gap-2 ${
                            selectedGroup === g
                              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                              : hasPreset
                                ? 'bg-yellow-50 dark:bg-yellow-900/10 hover:bg-yellow-100 dark:hover:bg-yellow-900/20 text-gray-600 dark:text-gray-400'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <div className="flex items-center gap-1.5 min-w-0">
                            {hasPreset && (
                              <span
                                title={`Auto-runs preset on upload${meta.automation.label ? ': ' + meta.automation.label : ''}`}
                                className="inline-block h-2 w-2 rounded-full bg-yellow-400 ring-1 ring-yellow-300 flex-shrink-0"
                              />
                            )}
                            {hasAgent && (
                              <span
                                title={`Auto-assigns to: ${meta.attachedAgent.name || meta.attachedAgent.id}`}
                                className="inline-block h-2 w-2 rounded-full bg-blue-500 ring-1 ring-blue-300 flex-shrink-0"
                              />
                            )}
                            <span className="truncate">{g}</span>
                          </div>
                          <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">{getGroupCount(g)}</span>
                        </button>
                        <div className="absolute right-0 top-0 opacity-0 invisible group-hover/grp:opacity-100 group-hover/grp:visible transition-all z-20">
                          <div className="relative">
                            <button
                              onClick={(e) => { e.stopPropagation(); setOpenGroupMenu(g === openGroupMenu ? null : g); }}
                              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400 hover:text-gray-600"
                            >
                              <EllipsisVerticalIcon className="h-4 w-4" />
                            </button>
                            {openGroupMenu === g && (
                              <div className="absolute right-0 top-6 w-44 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-30 py-1">
                                <button onClick={() => { handleRenameGroup(g); setOpenGroupMenu(null); }}
                                  className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                                  Rename
                                </button>
                                <button onClick={() => { toast('Manage presets via /dashboard/accounts'); setOpenGroupMenu(null); }}
                                  className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                                  Preset / Automation
                                </button>
                                <button onClick={() => { handleDeleteGroup(g); setOpenGroupMenu(null); }}
                                  className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                                  Delete Group
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                        <GroupStatusSidebarFilters
                          selectedGroup={selectedGroup}
                          groupKey={g}
                          filterStatus={statusFilter}
                          counts={getGroupStatusCounts(g)}
                          onSelect={(name, status) => {
                            setSelectedGroup(name);
                            setStatusFilter(status);
                            setModelFilter('all');
                            setVpsFilter('all');
                          }}
                        />
                      </div>
                    );
                  })}
                </div>

                {/* Add group */}
                {addingGroup ? (
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    const name = newGroupName.trim();
                    if (!name) return;
                    try {
                      await api.post('/accounts/groups/create-main', { name, subGroups: [] });
                      toast.success(`Group "${name}" created`);
                      setNewGroupName('');
                      setAddingGroup(false);
                      fetchGroupDetail();
                    } catch (err) {
                      toast.error(err.response?.data?.error || 'Failed to create group');
                    }
                  }} className="mt-2">
                    <input
                      autoFocus
                      type="text"
                      value={newGroupName}
                      onChange={e => setNewGroupName(e.target.value)}
                      placeholder="Group name…"
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                    <div className="flex gap-1 mt-1">
                      <button type="submit" className="btn btn-primary btn-sm flex-1">Create</button>
                      <button type="button" onClick={() => { setAddingGroup(false); setNewGroupName(''); }} className="btn btn-secondary btn-sm flex-1">Cancel</button>
                    </div>
                  </form>
                ) : (
                  <button
                    onClick={() => setAddingGroup(true)}
                    className="w-full mt-2 px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-md hover:border-primary-400 transition-colors"
                  >
                    + New Group
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Action toolbar — row 1: filters */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <FunnelIcon className={`h-4 w-4 flex-shrink-0 ${hasActiveFilters ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`} />
              {/* Search */}
              <div className="relative">
                <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search accounts…"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500 w-44"
                />
              </div>

              {/* Automation filter */}
              <select
                value={automationFilter}
                onChange={e => setAutomationFilter(e.target.value)}
                className="form-select text-sm py-1.5"
                title="Filter by automation status"
              >
                <option value="all">All Automations</option>
                <option value="running_stable">Running</option>
                <option value="starting">Starting</option>
                <option value="start_failed">Failed</option>
                <option value="active">Enabled (any)</option>
                <option value="inactive">Inactive</option>
              </select>

              {/* Status filter */}
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="form-select text-sm py-1.5"
                title="Filter by account status"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
                <option value="captcha">Captcha</option>
                <option value="xchat_key_error">Key Error</option>
                <option value="auth_expired">Auth Expired</option>
              </select>

              {/* Model filter */}
              <select
                value={modelFilter}
                onChange={e => setModelFilter(e.target.value)}
                className="form-select text-sm py-1.5"
                title="Filter by model"
              >
                <option value="all">All Models</option>
                {models.map(m => (
                  <option key={m._id} value={m._id}>
                    {m.displayName || m.name}
                  </option>
                ))}
              </select>

              {/* Clear filters */}
              {hasActiveFilters && (
                <button onClick={clearFilters} className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors px-2 py-1 rounded border border-gray-200 dark:border-gray-600">
                  <XCircleIcon className="h-3.5 w-3.5" />
                  Clear filters
                </button>
              )}

              {/* Spacer */}
              <div className="flex-1" />

              {/* Count */}
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {selectedAccounts.size > 0 ? `${selectedAccounts.size} selected` : `${cpTotalCount} accounts`}
              </span>
            </div>

            {/* Action toolbar — row 2: bulk actions */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <button
                onClick={() => handleBulkStart([...selectedAccounts])}
                disabled={cpOperationLoading || selectedAccounts.size === 0}
                className="btn btn-primary btn-sm"
              >
                <PlayIcon className="h-4 w-4 mr-1" />
                Start Selected
              </button>
              <button
                onClick={() => handleBulkStop([...selectedAccounts])}
                disabled={cpOperationLoading || selectedAccounts.size === 0}
                className="btn btn-danger btn-sm"
              >
                <StopIcon className="h-4 w-4 mr-1" />
                Stop Selected
              </button>
              <button
                onClick={async () => {
                  try {
                    setCpOperationLoading(true);
                    const allIds = await fetchAllFilteredIds();
                    await handleBulkStart(allIds);
                  } catch {
                    toast.error('Failed to fetch all account IDs');
                    setCpOperationLoading(false);
                  }
                }}
                disabled={cpOperationLoading}
                className="btn btn-primary btn-sm"
              >
                <PlayIcon className="h-4 w-4 mr-1" />
                Start All {hasActiveFilters ? '(filtered)' : ''}
              </button>
              <button
                onClick={async () => {
                  try {
                    setCpOperationLoading(true);
                    const allIds = await fetchAllFilteredIds();
                    await handleBulkStop(allIds);
                  } catch {
                    toast.error('Failed to fetch all account IDs');
                    setCpOperationLoading(false);
                  }
                }}
                disabled={cpOperationLoading}
                className="btn btn-danger btn-sm"
              >
                <StopIcon className="h-4 w-4 mr-1" />
                Stop All {hasActiveFilters ? '(filtered)' : ''}
              </button>
            </div>

            {/* Bulk-start progress bar */}
            {(bulkStartProgress.status === 'processing' || bulkStartProgress.status === 'queued' || bulkStartProgress.status === 'completed' || bulkStartProgress.status === 'cancelled') && bulkStartProgress.total > 0 && (
              <div className="mb-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <PlayIcon className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {bulkStartProgress.status === 'completed'
                        ? `Done — ${bulkStartProgress.successCount} started, ${bulkStartProgress.failCount} failed`
                        : bulkStartProgress.status === 'cancelled'
                        ? `Cancelled — ${bulkStartProgress.successCount} completed before stop`
                        : bulkStartProgress.status === 'queued'
                        ? `Queued ${bulkStartProgress.total} accounts — starting soon…`
                        : `${bulkStartProgress.currentAccount || 'Starting…'} (${bulkStartProgress.current}/${bulkStartProgress.total})`}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 dark:text-gray-400">{bulkStartProgress.current}/{bulkStartProgress.total}</span>
                    {(bulkStartProgress.status === 'processing' || bulkStartProgress.status === 'queued') && (
                      <button
                        onClick={handleBulkStartCancel}
                        className="text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium flex items-center gap-1"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-1.5">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      bulkStartProgress.status === 'completed'
                        ? bulkStartProgress.failCount > 0 ? 'bg-yellow-500' : 'bg-green-500'
                        : bulkStartProgress.status === 'cancelled'
                        ? 'bg-orange-500'
                        : 'bg-primary-600'
                    }`}
                    style={{ width: `${Math.max(0, Math.min(100, (bulkStartProgress.current / bulkStartProgress.total) * 100))}%` }}
                  />
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                    <span className="text-green-600 dark:text-green-400 font-medium">{bulkStartProgress.successCount} succeeded</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                    <span className="text-red-600 dark:text-red-400 font-medium">{bulkStartProgress.failCount} failed</span>
                  </span>
                </div>
              </div>
            )}

            {/* Select all banner */}
            {selectedAccounts.size > 0 && selectedAccounts.size === cpAccounts.length && cpTotalCount > 50 && !allAccountsSelected && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md px-4 py-2 mb-3 flex items-center justify-between">
                <span className="text-sm text-blue-700 dark:text-blue-300">
                  {selectedAccounts.size} accounts on this page selected.
                </span>
                <button
                  onClick={async () => {
                    try {
                      const allIds = await fetchAllFilteredIds();
                      setSelectedAccounts(new Set(allIds));
                      setAllAccountsSelected(true);
                    } catch {
                      toast.error('Failed to select all accounts');
                    }
                  }}
                  className="text-sm font-medium text-blue-700 dark:text-blue-300 underline"
                >
                  Select all {cpTotalCount} accounts{hasActiveFilters ? ' (filtered)' : ''}
                </button>
              </div>
            )}

            {/* Account table */}
            {cpLoading && cpAccounts.length === 0 ? (
              <div className="flex items-center justify-center h-48">
                <div className="loading-spinner h-8 w-8" />
              </div>
            ) : (
              <div className="card overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left w-10">
                        <input
                          type="checkbox"
                          className="rounded"
                          checked={cpAccounts.length > 0 && selectedAccounts.size === cpAccounts.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAccounts(new Set(cpAccounts.map(a => String(a._id))));
                            } else {
                              setSelectedAccounts(new Set());
                            }
                          }}
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Username</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Model</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Chatbot</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800">
                    {cpAccounts.map(account => {
                      const isSelected = selectedAccounts.has(String(account._id));
                      const isRunning = account.automation?.enabled === true && !account.automation?.starting && account.status === 'active';
                      const isStarting = account.automation?.starting === true;
                      const isFailed = account.automationStartFailed === true && !account.automation?.enabled;
                      return (
                        <tr key={account._id} className={`transition-colors ${isSelected ? 'bg-primary-50 dark:bg-primary-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}>
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              className="rounded"
                              checked={isSelected}
                              onChange={() => {
                                setSelectedAccounts(prev => {
                                  const next = new Set(prev);
                                  next.has(String(account._id)) ? next.delete(String(account._id)) : next.add(String(account._id));
                                  return next;
                                });
                              }}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              @{account.username}
                            </div>
                            {account.displayName && account.displayName !== account.username && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">{account.displayName}</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {account.modelId?.name || <span className="text-gray-400 italic">No model</span>}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`badge ${getStatusBadge(account)}`}>
                              {getStatusLabel(account.status)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {isStarting ? (
                              <span className="badge badge-yellow">Starting…</span>
                            ) : isRunning ? (
                              <span className="badge badge-green">Running</span>
                            ) : isFailed ? (
                              <span className="badge badge-danger">Failed</span>
                            ) : account.automation?.enabled ? (
                              <span className="badge badge-blue">Enabled</span>
                            ) : (
                              <span className="badge badge-gray">Off</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              {account.automation?.enabled || isStarting ? (
                                <button
                                  onClick={() => handleBulkStop([String(account._id)])}
                                  disabled={cpOperationLoading}
                                  className="btn btn-danger btn-sm"
                                >
                                  <StopIcon className="h-3.5 w-3.5 mr-1" />
                                  Stop
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleBulkStart([String(account._id)])}
                                  disabled={cpOperationLoading}
                                  className="btn btn-primary btn-sm"
                                >
                                  <PlayIcon className="h-3.5 w-3.5 mr-1" />
                                  Start
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {cpAccounts.length === 0 && !cpLoading && (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <UserIcon className="mx-auto h-10 w-10 mb-2 opacity-40" />
                    <p className="text-sm">No accounts match your filters</p>
                    {hasActiveFilters && (
                      <button onClick={clearFilters} className="mt-2 text-xs text-primary-600 dark:text-primary-400 underline">
                        Clear filters
                      </button>
                    )}
                  </div>
                )}
                {/* CP Pagination */}
                {cpTotalCount > 50 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Showing {(cpPage - 1) * 50 + 1}–{Math.min(cpPage * 50, cpTotalCount)} of {cpTotalCount}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCpPage(p => Math.max(1, p - 1))}
                        disabled={cpPage === 1}
                        className="btn btn-secondary btn-sm"
                      >Previous</button>
                      <span className="text-sm text-gray-700 dark:text-gray-300">Page {cpPage} of {Math.ceil(cpTotalCount / 50)}</span>
                      <button
                        onClick={() => setCpPage(p => Math.min(Math.ceil(cpTotalCount / 50), p + 1))}
                        disabled={cpPage >= Math.ceil(cpTotalCount / 50)}
                        className="btn btn-secondary btn-sm"
                      >Next</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'versions' && (
        <ChatbotVersionsTab models={models} />
      )}

      {/* ---- Conversations tab ---- */}
      {activeTab === 'conversations' && (
        <>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Account:
              </label>
              <select
                value={conversationFilter}
                onChange={(e) => { setConversationFilter(e.target.value); setConvPage(1); }}
                className="form-select"
              >
                <option value="all">All Accounts</option>
                {accounts.map(a => (
                  <option key={a._id} value={a._id}>
                    {a.displayName || a.username}
                  </option>
                ))}
              </select>

              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Source:
              </label>
              <select
                value={sourceFilter}
                onChange={(e) => { setSourceFilter(e.target.value); setConvPage(1); }}
                className="form-select"
              >
                <option value="all">All</option>
                <option value="automation">X</option>
                <option value="chatlink">Chat link</option>
                <option value="api">API</option>
              </select>

              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Account Status:
              </label>
              <select
                value={convAccountStatusFilter}
                onChange={(e) => { setConvAccountStatusFilter(e.target.value); setConvPage(1); }}
                className="form-select"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
                <option value="captcha">Captcha</option>
                <option value="xchat_key_error">Key Error</option>
                <option value="auth_expired">Auth Expired</option>
              </select>

              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Phase:
              </label>
              <select
                value={phaseFilter}
                onChange={(e) => { setPhaseFilter(e.target.value); setConvPage(1); }}
                className="form-select"
              >
                <option value="all">All Phases</option>
                {CONVERSATION_PHASES.map(phase => (
                  <option key={phase} value={phase}>{getPhaseLabel(phase)}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={fetchConversations}
                disabled={conversationsLoading}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
              >
                <ArrowPathIcon className={`h-4 w-4 mr-1.5 ${conversationsLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {convTotal.toLocaleString()} conversation{convTotal !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          <div
            ref={conversationsListRef}
            className="overflow-y-auto pr-1"
            style={{ maxHeight: '70vh' }}
          >
            {conversationsLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="loading-spinner h-8 w-8" />
              </div>
            ) : (
              <div className="card overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Account</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Model</th>
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        title="Raw message count (bot + user). Real exchanges ≈ Messages ÷ 2. Hard stop fires at 400 exchanges."
                      >Messages</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Activity</th>
                      <th className="px-4 py-3 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800">
                    {conversations.map(conv => (
                      <React.Fragment key={conv._id}>
                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors border-b-0">
                          <td className="px-4 pt-3 pb-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                            {conv.source === 'chatlink' ? (
                              <span className="inline-flex items-center gap-2">
                                <span className="badge badge-blue text-xs">Chat link</span>
                                <span>{conv.chatLinkId?.platformSlug || conv.chatLinkId?.customDomain || 'link'}</span>
                              </span>
                            ) : (
                              <>@{conv.accountId?.username || '—'}</>
                            )}
                          </td>
                          <td className="px-4 pt-3 pb-1 text-sm text-gray-600 dark:text-gray-400">
                            @{conv.xUsername || '—'}
                          </td>
                          <td className="px-4 pt-3 pb-1 text-sm text-gray-600 dark:text-gray-400">
                            {conv.modelId?.name || '—'}
                          </td>
                          <td className="px-4 pt-3 pb-1 text-sm text-gray-600 dark:text-gray-400">
                            {conv.stats?.totalMessages || 0}
                          </td>
                          <td className="px-4 pt-3 pb-1">
                            {conv.isConverted ? (
                              <span className="badge badge-success text-xs">Converted</span>
                            ) : conv.conversationPhase === 'completed' ? (
                              <span className="badge badge-gray text-xs">Completed</span>
                            ) : (
                              <span className="badge badge-blue text-xs">Active</span>
                            )}
                          </td>
                          <td className="px-4 pt-3 pb-1 text-xs text-gray-500 dark:text-gray-400">
                            {conv.lastActivity
                              ? new Date(conv.lastActivity).toLocaleString()
                              : conv.messages?.length > 0
                                ? new Date(conv.messages[conv.messages.length - 1].timestamp).toLocaleString()
                                : '—'}
                          </td>
                          <td className="px-4 pt-3 pb-1">
                            <button
                              onClick={() => toggleConversationExpand(conv._id)}
                              className="text-blue-500 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                              title="View conversation"
                            >
                              {expandedConvId === conv._id
                                ? <EyeSlashIcon className="h-4 w-4" />
                                : <EyeIcon className="h-4 w-4" />}
                            </button>
                          </td>
                        </tr>
                        {/* Progress bar row — always visible, spans full width */}
                        <tr className="border-b border-gray-100 dark:border-gray-800">
                          <td colSpan={7} className="px-0 pt-0 pb-0">
                            <ConversationProgressLine conversation={conv} />
                          </td>
                        </tr>
                        {expandedConvId === conv._id && (
                          <tr>
                            <td colSpan={7} className="px-6 pb-4 pt-2 bg-gray-50 dark:bg-gray-900/50">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {(conv.messages ? conv.messages.length : (conv.stats?.totalMessages || 0))} messages · Day {conv.currentDay}
                                </span>
                                <button
                                  onClick={() => handleDeleteConversation(conv._id)}
                                  className="btn btn-danger btn-sm"
                                >
                                  <TrashIcon className="h-3.5 w-3.5 mr-1" />Clean
                                </button>
                              </div>
                              <LeadProfilePanel
                                profile={conv.userProfile}
                                userType={conv.userType}
                                isDarkMode={isDarkMode}
                              />
                              <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
                                {convDetailLoadingId === conv._id && !conv.messages && (
                                  <div className="text-xs text-gray-400 italic">Loading messages…</div>
                                )}
                                {[...(conv.messages || [])].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)).map((msg, idx) => {
                                  if (msg.contentType === 'system') {
                                    return (
                                      <div key={idx} className="text-center text-[11px] text-gray-400 py-1">
                                        {msg.content}
                                      </div>
                                    );
                                  }
                                  const isBot = msg.sender === 'bot';
                                  const hasPhoto = msg.photosShared && msg.photosShared.length > 0;
                                  const isImageMsg = msg.contentType === 'image';
                                  const isAudioMsg = msg.contentType === 'audio';
                                  return (
                                    <div key={idx} className={`flex ${isBot ? 'justify-end' : 'justify-start'}`}>
                                      <div className={`max-w-sm rounded-lg text-xs overflow-hidden ${
                                        isBot
                                          ? 'bg-blue-500 text-white'
                                          : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white'
                                      }`}>
                                        <div className="px-3 pt-2 font-medium opacity-70 flex items-center gap-1.5">
                                          {isBot ? (conv.modelId?.name || 'Bot') : (conv.xUsername || 'User')}
                                          {msg.isFollowUp && (
                                            <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-white/20 tracking-wide">
                                              {msg.followUpType === 'afterCta' ? 'after-cta follow-up'
                                                : msg.followUpType === 'sleep_wake' ? 'morning ping'
                                                : msg.followUpType === 'busy_return' ? 'busy check-in'
                                                : msg.followUpType === 'idle' ? 'idle ping'
                                                : 'follow-up'}
                                            </span>
                                          )}
                                        </div>
                                        {/* Image — contentType === 'image' (API chat path) */}
                                        {isImageMsg && msg.content && (
                                          <div className="px-2 pb-1">
                                            <img
                                              src={msg.content}
                                              alt="Media"
                                              className="rounded h-28 w-28 object-cover"
                                              onError={e => { e.target.style.display = 'none'; }}
                                            />
                                          </div>
                                        )}
                                        {isAudioMsg && msg.content && (
                                          <div className="px-2 pb-1">
                                            <audio controls src={msg.content} className="h-8 max-w-full" />
                                            {msg.transcript && (
                                              <div className="px-1 pt-1 text-[10px] opacity-80">{msg.transcript}</div>
                                            )}
                                          </div>
                                        )}
                                        {/* Photos — photosShared[] (X DM automation path) */}
                                        {hasPhoto && (
                                          <div className="flex flex-wrap gap-1 px-2 pb-1">
                                            {msg.photosShared.map((p, pi) => p.url && (
                                              <img
                                                key={pi}
                                                src={p.url}
                                                alt={p.category || 'Photo'}
                                                className="rounded h-24 w-24 object-cover"
                                                onError={e => { e.target.style.display = 'none'; }}
                                              />
                                            ))}
                                          </div>
                                        )}
                                        {/* Text content (shown unless it's a pure image-only message) */}
                                        {(!isImageMsg && !isAudioMsg && msg.content) && (
                                          <div className="px-3 pb-2">{msg.content}</div>
                                        )}
                                        <div className="px-3 pb-2 text-[10px] opacity-60 text-right">
                                          {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                                {convDetailLoadingId !== conv._id && (!conv.messages || conv.messages.length === 0) && (
                                  <div className="text-xs text-gray-400 italic">No messages stored</div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
                {conversations.length === 0 && !conversationsLoading && (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <ChatBubbleLeftRightIcon className="mx-auto h-10 w-10 mb-2 opacity-40" />
                    <p className="text-sm">
                      {(conversationFilter !== 'all' || sourceFilter !== 'all' || phaseFilter !== 'all' || convAccountStatusFilter !== 'all')
                        ? 'No conversations match your filters'
                        : 'Conversations will appear here when users start messaging your accounts.'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Conversations pagination — server-side across ALL conversations */}
          {convTotal > CONV_PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 mt-3">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Showing {(convPage - 1) * CONV_PAGE_SIZE + 1}–{Math.min(convPage * CONV_PAGE_SIZE, convTotal)} of {convTotal.toLocaleString()}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setConvPage(p => Math.max(1, p - 1))}
                  disabled={convPage === 1 || conversationsLoading}
                  className="btn btn-secondary btn-sm"
                >Previous</button>
                <span className="text-sm text-gray-700 dark:text-gray-300">Page {convPage} of {convTotalPages}</span>
                <button
                  onClick={() => setConvPage(p => Math.min(convTotalPages, p + 1))}
                  disabled={convPage >= convTotalPages || conversationsLoading}
                  className="btn btn-secondary btn-sm"
                >Next</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ---- Blacklist tab (admin) ---- */}
      {activeTab === 'blacklist' && isAdmin && (
        <>
          <div className="card p-4 mb-4 space-y-4">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={spamBlacklistEnabled}
                disabled={spamBlacklistLoading || spamBlacklistSaving}
                onChange={(e) => handleSpamBlacklistToggle(e.target.checked)}
                className="mt-1 rounded border-gray-300"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                  Automatic spam blacklist
                </span>
                <span className="block text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  When enabled, users who send the same message 4+ times in 24 hours are added to the global blacklist and skipped on all accounts.
                  When disabled (default), no users are auto-blacklisted and existing blacklist entries are ignored for replies.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={convIdSwitchFilterEnabled}
                disabled={spamBlacklistLoading || convIdSwitchFilterSaving}
                onChange={(e) => handleConvIdSwitchFilterToggle(e.target.checked)}
                className="mt-1 rounded border-gray-300"
              />
              <span>
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                  bot filter (conv ID switch)
                </span>
                <span className="block text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  When enabled, if the two halves of an X chat id (us vs them) are stored as xUserId twice on the same thread, we stop every conversation with that user and add them to the global blacklist so no other account can reply.
                </span>
              </span>
            </label>
          </div>

          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex-shrink-0">
              Global Blacklist
            </h2>
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search username or user ID…"
                value={blacklistSearchInput}
                onChange={(e) => setBlacklistSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setBlacklistSearch(blacklistSearchInput.trim());
                }}
                className="form-input pl-9 text-sm py-1.5 w-full"
              />
            </div>
            <button
              onClick={() => setBlacklistSearch(blacklistSearchInput.trim())}
              className="btn btn-secondary btn-sm"
            >
              Search
            </button>
            <button
              onClick={fetchBlacklistedUsers}
              disabled={blacklistLoading}
              className="btn btn-secondary btn-sm"
            >
              <ArrowPathIcon className={`h-4 w-4 mr-1.5 ${blacklistLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {blacklistTotal.toLocaleString()} blocked
            </span>
            <div className="flex-1" />
            {selectedBlacklist.size > 0 && (
              <button
                onClick={() => handleUnblockBlacklist([...selectedBlacklist])}
                disabled={blacklistUnblockLoading}
                className="btn btn-primary btn-sm"
              >
                Unblock selected ({selectedBlacklist.size})
              </button>
            )}
          </div>

          <div className="card overflow-hidden">
            {blacklistLoading ? (
              <div className="flex items-center justify-center py-16">
                <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-3 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={blacklistUsers.length > 0 && blacklistUsers.every(u => selectedBlacklist.has(u.xUserId))}
                        onChange={toggleAllBlacklistOnPage}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Reason</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Detected on</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Blocked</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Messages</th>
                    <th className="px-4 py-3 w-24"></th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800">
                  {blacklistUsers.map(entry => {
                    const preview = entry.conversation?.previewMessages || [];
                    const displayName = entry.xUsername && entry.xUsername !== 'unknown'
                      ? `@${entry.xUsername}`
                      : entry.xUserId;
                    const isExpanded = expandedBlacklistId === entry.xUserId;
                    return (
                      <React.Fragment key={entry.xUserId}>
                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                          <td className="px-3 py-3">
                            <input
                              type="checkbox"
                              checked={selectedBlacklist.has(entry.xUserId)}
                              onChange={() => toggleBlacklistSelection(entry.xUserId)}
                              className="rounded border-gray-300"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{displayName}</div>
                            {entry.xUsername && entry.xUsername !== 'unknown' && (
                              <div className="text-xs text-gray-400 font-mono">{entry.xUserId}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            <span className="badge badge-gray text-xs mr-1">{entry.reason}</span>
                            {entry.reasonLabel || entry.reason}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {entry.detectedByUsername ? `@${entry.detectedByUsername}` : (entry.detectedBy === 'system' ? 'System' : '—')}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                            {entry.detectedAt || entry.createdAt
                              ? new Date(entry.detectedAt || entry.createdAt).toLocaleString()
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {preview.length > 0
                              ? `${preview.length} shown`
                              : entry.conversation
                                ? `${entry.conversation.messageCount || 0} stored`
                                : 'No history'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 justify-end">
                              <button
                                onClick={() => setExpandedBlacklistId(prev => prev === entry.xUserId ? null : entry.xUserId)}
                                className="text-blue-500 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                                title="View messages"
                              >
                                {isExpanded
                                  ? <EyeSlashIcon className="h-4 w-4" />
                                  : <EyeIcon className="h-4 w-4" />}
                              </button>
                              <button
                                onClick={() => handleUnblockBlacklist([entry.xUserId])}
                                disabled={blacklistUnblockLoading}
                                className="btn btn-secondary btn-sm py-1 px-2 text-xs"
                              >
                                Unblock
                              </button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} className="px-6 pb-4 pt-2 bg-gray-50 dark:bg-gray-900/50">
                              {entry.conversation ? (
                                <>
                                  <div className="flex items-center justify-between mb-2 text-xs text-gray-500 dark:text-gray-400">
                                    <span>
                                      @{entry.conversation.accountUsername || '—'}
                                      {entry.conversation.modelName ? ` · ${entry.conversation.modelName}` : ''}
                                      {entry.conversation.conversationPhase ? ` · ${entry.conversation.conversationPhase}` : ''}
                                    </span>
                                    <span>{entry.conversation.messageCount || 0} messages in DB</span>
                                  </div>
                                  <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
                                    {preview.map((msg, idx) => {
                                      const isBot = msg.sender === 'bot';
                                      return (
                                        <div key={idx} className={`flex ${isBot ? 'justify-end' : 'justify-start'}`}>
                                          <div className={`max-w-sm rounded-lg text-xs px-3 py-2 ${
                                            isBot
                                              ? 'bg-blue-500 text-white'
                                              : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white'
                                          }`}>
                                            <div className="font-medium opacity-70 mb-0.5">
                                              {isBot ? (entry.conversation.modelName || 'Bot') : (entry.xUsername !== 'unknown' ? entry.xUsername : 'User')}
                                            </div>
                                            <div>{msg.content || '(no text)'}</div>
                                            {msg.timestamp && (
                                              <div className="text-[10px] opacity-60 text-right mt-1">
                                                {new Date(msg.timestamp).toLocaleString()}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                    {preview.length === 0 && (
                                      <div className="text-xs text-gray-400 italic">No messages stored for this conversation</div>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <div className="text-xs text-gray-500 dark:text-gray-400 italic py-2">
                                  No conversation stored in the database for this user.
                                  {entry.detectedByUsername && (
                                    <span> Block was triggered on @{entry.detectedByUsername}.</span>
                                  )}
                                  {' '}Unblock will queue an inbox scan to detect and reply to the latest message.
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
            {!blacklistLoading && blacklistUsers.length === 0 && (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <NoSymbolIcon className="mx-auto h-10 w-10 mb-2 opacity-40" />
                <p className="text-sm">
                  {blacklistSearch ? 'No blacklisted users match your search' : 'No blacklisted users'}
                </p>
              </div>
            )}
          </div>

          {blacklistTotal > BL_PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 mt-3">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Showing {(blacklistPage - 1) * BL_PAGE_SIZE + 1}–{Math.min(blacklistPage * BL_PAGE_SIZE, blacklistTotal)} of {blacklistTotal.toLocaleString()}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setBlacklistPage(p => Math.max(1, p - 1))}
                  disabled={blacklistPage === 1 || blacklistLoading}
                  className="btn btn-secondary btn-sm"
                >Previous</button>
                <span className="text-sm text-gray-500">Page {blacklistPage} of {blacklistTotalPages}</span>
                <button
                  onClick={() => setBlacklistPage(p => Math.min(blacklistTotalPages, p + 1))}
                  disabled={blacklistPage >= blacklistTotalPages || blacklistLoading}
                  className="btn btn-secondary btn-sm"
                >Next</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ---- Error Log tab ---- */}
      {activeTab === 'errors' && (
        <div>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex-shrink-0">
              Error Log
            </h2>
            {/* Type filter */}
            <select
              value={errorTypeFilter}
              onChange={e => setErrorTypeFilter(e.target.value)}
              className="form-select text-sm py-1.5"
            >
              <option value="all">All types ({errorLog.length})</option>
              {['xchat_key_error','bulk_start_failed','reply_failed','process_dm_error','chatbot_error'].map(t => {
                const count = errorLog.filter(e => e.type === t).length;
                if (count === 0) return null;
                return <option key={t} value={t}>{ERROR_TYPE_LABELS[t] || t} ({count})</option>;
              })}
            </select>
            <div className="flex-1" />
            <button
              onClick={() => setErrorLog([])}
              className="btn btn-danger btn-sm"
              disabled={errorLog.length === 0}
            >
              <XMarkIcon className="h-4 w-4 mr-1" />
              Clear all
            </button>
          </div>

          {errorLog.length === 0 ? (
            <div className="text-center py-16">
              <CheckCircleIcon className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" />
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                No errors recorded this session
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Errors from bulk start failures, xchat key issues, and reply failures will appear here in real-time
              </p>
            </div>
          ) : (() => {
            const filtered = errorTypeFilter === 'all' ? errorLog : errorLog.filter(e => e.type === errorTypeFilter);
            return (
              <div className="space-y-2">
                {filtered.map(entry => {
                  const typeMeta = ERROR_TYPE_META[entry.type] || ERROR_TYPE_META.chatbot_error;
                  const isExpanded = expandedErrors.has(entry.id);
                  const hasDetail = !!(entry.detail && Object.keys(entry.detail).length > 0);
                  return (
                    <div key={entry.id} className={`card border-l-4 ${typeMeta.border}`}>
                      <div className="card-body py-3 px-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            {/* Top row: type badge + account + time */}
                            <div className="flex items-center flex-wrap gap-2 mb-1">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${typeMeta.badge}`}>
                                <ExclamationTriangleIcon className="h-3 w-3" />
                                {typeMeta.label}
                              </span>
                              {entry.username && entry.username !== 'unknown' && (
                                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                  @{entry.username}
                                </span>
                              )}
                              <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">
                                {new Date(entry.timestamp).toLocaleTimeString()} · {new Date(entry.timestamp).toLocaleDateString()}
                              </span>
                            </div>
                            {/* Error message */}
                            <p className={`text-sm font-medium ${typeMeta.text}`}>{entry.message}</p>
                            {/* Inline key detail fields */}
                            {entry.detail && (
                              <div className="flex flex-wrap gap-3 mt-1.5">
                                {entry.detail.recipient && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    <span className="font-medium">To:</span> @{entry.detail.recipient}
                                  </span>
                                )}
                                {entry.detail.conversationId && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    <span className="font-medium">Conv:</span> {entry.detail.conversationId}
                                  </span>
                                )}
                                {entry.detail.classification && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    <span className="font-medium">Class:</span> {entry.detail.classification}
                                  </span>
                                )}
                                {entry.detail.messagePreview && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400 italic truncate max-w-xs">
                                    "{entry.detail.messagePreview}"
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          {hasDetail && (
                            <button
                              onClick={() => toggleErrorExpand(entry.id)}
                              className="btn btn-secondary btn-sm flex-shrink-0 text-xs"
                            >
                              {isExpanded ? <EyeSlashIcon className="h-3.5 w-3.5" /> : <EyeIcon className="h-3.5 w-3.5" />}
                            </button>
                          )}
                        </div>

                        {isExpanded && hasDetail && (
                          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Full Detail</p>
                            <pre className="text-xs bg-gray-50 dark:bg-gray-900 rounded p-2.5 overflow-x-auto text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                              {JSON.stringify(entry.detail, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

    </div>
  );
};

export default Chatbot;
